import React, { useCallback, useRef } from "react";
import { FieldValues, Path, PathValue, useFormContext } from "react-hook-form";
import {
    components,
    GroupBase,
    InputActionMeta,
    InputProps,
    SingleValue
} from "react-select";
import Select from "react-select/base";

import { MenuComponent } from "@/components/Form/Base/components/selectMenu.component";
import { CreatableSelect } from "@/components/Form/Base/creatableSelect.component";

import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";
import { useNumericField } from "../NumericField/NumericField.hooks";
import { BaseOption } from "../SelectField/selectField.component";

type CreatableSelectFieldInputProps<
    T extends FieldValues,
    TOption extends BaseOption<PathValue<T, Path<T>>>
> = Pick<
    InputProps<TOption, false>,
    "pattern" | "inputMode" | "type" | "onKeyDown" | "autoComplete"
> & {
    mutateOnBlur?: (inputValue: string) => PathValue<T, Path<T>>;
    onChange?: ReturnType<typeof useNumericField>["onChangeHandler"];
    onPaste?: ReturnType<typeof useNumericField>["onPasteHandler"];
};

export type CreatableSelectFieldProps<
    T extends FieldValues,
    TOption extends BaseOption<PathValue<T, Path<T>>>
> = FormValuesExploded<T> & {
    optionsList: Array<TOption>;
    disabled?: boolean;
    shouldFocus?: boolean;
    className?: string;
    placeholder?: string;
    allowNew?: boolean;
    inputProps?: CreatableSelectFieldInputProps<T, TOption>;
};

const CreatableSelectField = <
    T extends FieldValues,
    TOption extends BaseOption<PathValue<T, Path<T>>>
>({
    label,
    name,
    microCopy,
    required,
    hideLabel,
    optionalIndication,
    optionsList,
    disabled = false,
    className,
    placeholder,
    inputProps = {}
}: CreatableSelectFieldProps<T, TOption>) => {
    const selectRef = useRef<Select<TOption, false, GroupBase<TOption>>>(null);
    const { setValue, getValues, trigger } = useFormContext<T>();

    const CustomInput = useCallback(
        (props: InputProps<TOption, false>) => {
            const newProps: Partial<InputProps<TOption, false>> = {};
            const {
                onChange: inputPropsOnChange,
                mutateOnBlur: _mutateOnBlur,
                ...restInputProps
            } = inputProps;

            if (inputPropsOnChange) {
                newProps.onChange = (
                    e: React.ChangeEvent<HTMLInputElement>
                ) => {
                    inputPropsOnChange(getValues(name), props.onChange!)(e);
                };
            }
            return (
                <components.Input
                    {...props}
                    {...restInputProps}
                    {...newProps}
                    isHidden={false}
                />
            );
        },
        [inputProps, getValues, name]
    );

    const handleOnChange = useCallback(
        (option: SingleValue<TOption>) => {
            setValue(
                name,
                (option ? option.value : null) as PathValue<T, Path<T>>,
                {
                    shouldValidate: false,
                    shouldDirty: true
                }
            );

            trigger(name);
        },
        [name, setValue, trigger]
    );

    const handleOnInputChange = (
        newValue: string,
        actionMeta: InputActionMeta
    ) => {
        switch (actionMeta.action) {
            case "input-change":
                setValue(name, newValue as PathValue<T, Path<T>>);
                break;
            case "input-blur":
                if (inputProps.mutateOnBlur) {
                    const newValueMutated = inputProps.mutateOnBlur(
                        getValues(name)
                    );
                    setValue(name, newValueMutated);
                }
                break;
            case "set-value":
            case "menu-close":
                break;
        }
    };

    return (
        <BaseField<T, Path<T>>
            label={label}
            name={name}
            microCopy={microCopy}
            required={required}
            hideLabel={hideLabel}
            optionalIndication={optionalIndication}
        >
            {({ field, fieldState }) => {
                const valueOption =
                    optionsList &&
                    field.value &&
                    optionsList.find((option) => option.value === field.value);

                return (
                    <CreatableSelect<TOption>
                        {...field}
                        ref={selectRef}
                        id={field.name}
                        name={field.name}
                        openMenuOnFocus={true}
                        classNamePrefix={"creatable-select-box"}
                        className={className}
                        isInvalid={fieldState.isInvalid}
                        isValid={fieldState.isValid}
                        onKeyDown={inputProps?.onKeyDown}
                        value={valueOption}
                        inputValue={field.value?.toString() || ""}
                        isSearchable={true}
                        options={optionsList}
                        isDisabled={disabled}
                        placeholder={placeholder}
                        blurInputOnSelect={true}
                        formatCreateLabel={(inputValue) => inputValue}
                        createOptionPosition={"first"}
                        onChange={handleOnChange}
                        components={{
                            Input: CustomInput,
                            Menu: MenuComponent
                        }}
                        onInputChange={handleOnInputChange}
                    />
                );
            }}
        </BaseField>
    );
};

export { CreatableSelectField };
